import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { IconHeart } from '../components/Icons';
import { Link, useParams } from 'react-router-dom';
import { toApiError } from '../api/client';
import { enquiryApi, meApi, propertyApi } from '../api/endpoints';
import type { PropertyDetail, RoomType } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ConfirmDialog, Field, ShareButton } from '../components/Common';
import { PropertyMap } from '../components/MapView';
import { StarRating } from '../components/PropertyCard';
import { DetailSkeleton, ErrorState, InlineError, InlineSuccess } from '../components/States';
import {
  availabilityLabel,
  chargeFrequencyLabel,
  directionsLink,
  humanise,
  money,
  plainDate,
  priceRange,
  relativeTime,
  sharingLabel,
  whatsAppLink,
} from '../utils/format';

export function PropertyDetailPage() {
  const { slug = '' } = useParams();
  const { user, isAuthenticated } = useAuth();

  const [property, setProperty] = useState<PropertyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const detail = await propertyApi.get(slug);
      setProperty(detail);
      setPhotoIndex(0);
    } catch (err) {
      const apiError = toApiError(err);
      setError(
        apiError.status === 404
          ? 'This listing is no longer available. It may have expired or been removed.'
          : apiError.message,
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isAuthenticated || !property) return;

    meApi
      .favouriteIds()
      .then((ids) => setSaved(ids.includes(property.id)))
      .catch(() => setSaved(false));
  }, [isAuthenticated, property]);

  if (loading) {
    return (
      <div className="container">
        <DetailSkeleton />
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="container">
        <ErrorState title="Listing unavailable" message={error ?? 'Not found.'} onRetry={load} />
        <div className="text-center mt-3">
          <Link to="/" className="btn btn-outline-primary">
            Back to search
          </Link>
        </div>
      </div>
    );
  }

  const availability = availabilityLabel(
    property.availabilityConfidence,
    property.availableUnits,
    property.category === 'Hotel' ? 'rooms' : 'beds',
  );

  const toggleSave = async () => {
    if (!isAuthenticated) {
      window.location.href = `/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    try {
      setSaved(await propertyApi.toggleFavourite(property.id));
    } catch {
      /* leave the page as it is */
    }
  };

  const photos = property.photos;

  return (
    <div className="container">
      <nav aria-label="Breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0 small">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={property.category === 'PgHostel' ? '/pg' : property.category === 'HomeRental' ? '/homes' : '/hotels'}>
              {property.category === 'PgHostel' ? 'PG & Hostels' : property.category === 'HomeRental' ? 'Homes' : 'Hotels'}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {property.locality}
          </li>
        </ol>
      </nav>

      <div className="row g-4">
        <div className="col-12 col-lg-8">
          {/* --- Gallery --- */}
          {photos.length > 0 ? (
            <div className="mb-4">
              <div className="sf-gallery__main">
                <img
                  src={photos[photoIndex].url}
                  alt={photos[photoIndex].altText ?? `${property.title}, photo ${photoIndex + 1}`}
                />

                {photos.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="sf-gallery__nav sf-gallery__nav--prev"
                      aria-label="Previous photo"
                      onClick={() => setPhotoIndex((i) => (i - 1 + photos.length) % photos.length)}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="sf-gallery__nav sf-gallery__nav--next"
                      aria-label="Next photo"
                      onClick={() => setPhotoIndex((i) => (i + 1) % photos.length)}
                    >
                      ›
                    </button>
                    <span className="sf-gallery__counter">
                      {photoIndex + 1} / {photos.length}
                    </span>
                  </>
                )}
              </div>

              {photos[photoIndex].caption && (
                <p className="form-text mt-1 mb-0">{photos[photoIndex].caption}</p>
              )}

              {photos.length > 1 && (
                <div className="sf-gallery__thumbs">
                  {photos.map((photo, index) => (
                    <button
                      type="button"
                      key={photo.id}
                      className="sf-gallery__thumb"
                      aria-current={index === photoIndex}
                      aria-label={`Show photo ${index + 1}: ${humanise(photo.category)}`}
                      onClick={() => setPhotoIndex(index)}
                    >
                      <img src={photo.thumbnailUrl ?? photo.url} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="sf-card sf-card-pad text-center sf-muted mb-4">No photos have been added yet.</div>
          )}

          {/* --- Heading --- */}
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
            <div>
              <h1 className="h3 mb-1">{property.title}</h1>
              <p className="sf-muted mb-2">
                {property.addressLine1}
                {property.addressLine2 && <>, {property.addressLine2}</>}, {property.locality}, {property.city}{' '}
                {property.pincode}
                {property.hotelDetail && (
                  <StarRating rating={property.hotelDetail.starRating} source={property.hotelDetail.starRatingSource} />
                )}
              </p>
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn ${saved ? 'btn-danger' : 'btn-outline-secondary'}`}
                aria-pressed={saved}
                onClick={toggleSave}
              >
                <><IconHeart fill={saved ? 'currentColor' : 'none'} /> {saved ? 'Saved' : 'Save'}</>
              </button>
              <ShareButton title={property.title} />
            </div>
          </div>

          {property.category !== 'HomeRental' && (
            <p className="mb-3">
              <span className={`sf-pill sf-pill--${availability.tone}`}>{availability.text}</span>{' '}
              <span className="sf-muted small">
                Last updated {relativeTime(property.lastAvailabilityUpdateUtc)}
              </span>
            </p>
          )}

          {property.description && <p className="mb-4">{property.description}</p>}

          {/* --- Category specifics --- */}
          {property.pgDetail && <PgFacts property={property} />}
          {property.homeDetail && <HomeFacts property={property} />}
          {property.hotelDetail && <HotelFacts property={property} />}

          {/* --- Room types --- */}
          {property.roomTypes.length > 0 && (
            <section className="mb-4">
              <h2 className="sf-section-title">
                {property.category === 'Hotel' ? 'Rooms' : 'Sharing options'}
              </h2>

              <div className="d-flex flex-column gap-3">
                {property.roomTypes.map((roomType) => (
                  <RoomTypeCard key={roomType.id} roomType={roomType} isHotel={property.category === 'Hotel'} />
                ))}
              </div>
            </section>
          )}

          {/* --- Amenities --- */}
          {property.amenities.length > 0 && (
            <section className="mb-4">
              <h2 className="sf-section-title">Amenities</h2>
              {Object.entries(groupBy(property.amenities, (a) => a.group)).map(([group, items]) => (
                <div className="mb-2" key={group}>
                  <h3 className="small text-uppercase sf-muted fw-bold">{group}</h3>
                  <div className="d-flex flex-wrap gap-2">
                    {items.map((amenity) => (
                      <span className="sf-chip" key={amenity.id}>
                        {amenity.name}
                        {amenity.notes && <span className="sf-muted"> ({amenity.notes})</span>}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* --- Location --- */}
          <section className="mb-4">
            <h2 className="sf-section-title">Location</h2>
            <PropertyMap latitude={property.latitude} longitude={property.longitude} title={property.title} />
            {property.landmark && <p className="form-text">Landmark: {property.landmark}</p>}
            <a
              className="btn btn-outline-secondary btn-sm mt-2"
              href={directionsLink(property.latitude, property.longitude, `${property.addressLine1}, ${property.locality}`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get directions
            </a>
          </section>

          <button type="button" className="btn btn-link text-danger px-0" onClick={() => setReportOpen(true)}>
            Report this listing
          </button>
        </div>

        {/* --- Sidebar --- */}
        <div className="col-12 col-lg-4">
          <div className="sf-sticky-aside d-flex flex-column gap-3">
            <PriceCard property={property} />
            <ContactCard property={property} defaultName={user?.fullName} defaultPhone={user?.phone} defaultEmail={user?.email} />
          </div>
        </div>
      </div>

      <ReportDialog
        open={reportOpen}
        propertyId={property.id}
        onClose={() => setReportOpen(false)}
      />
    </div>
  );
}

// =====================================================================================
// Sidebar cards
// =====================================================================================

function PriceCard({ property }: { property: PropertyDetail }) {
  const breakdown = property.costBreakdown;

  return (
    <div className="sf-card sf-card-pad">
      <div className="h4 mb-1">{priceRange(property.priceFrom, property.priceTo, property.priceUnit)}</div>

      {property.category === 'Hotel' && <p className="form-text mb-2">Taxes and mandatory fees included.</p>}

      {breakdown && (
        <>
          <h3 className="sf-section-title mt-3 mb-2">What you pay to move in</h3>

          {breakdown.lines.map((line, index) => (
            <div className="sf-cost-line" key={`${line.label}-${index}`}>
              <span>
                {line.label}
                <span className="sf-muted small"> · {chargeFrequencyLabel(line.frequency)}</span>
                {!line.isMandatory && <span className="sf-muted small"> · optional</span>}
              </span>
              <span className={line.isMandatory ? '' : 'sf-muted'}>{money(line.amount)}</span>
            </div>
          ))}

          <div className="sf-cost-total">
            <span>First payment</span>
            <span>{money(breakdown.firstPaymentTotal)}</span>
          </div>

          <p className="form-text mb-0 mt-2">
            {money(breakdown.monthlyRecurring)} per month afterwards. Optional charges are listed but not included in
            these totals.
          </p>
        </>
      )}

      {property.charges.length > 0 && !breakdown && (
        <>
          <h3 className="sf-section-title mt-3 mb-2">Additional charges</h3>
          {property.charges.map((charge) => (
            <div className="sf-cost-line" key={charge.id}>
              <span>
                {charge.label}
                <span className="sf-muted small"> · {chargeFrequencyLabel(charge.frequency)}</span>
              </span>
              <span>{money(charge.amount)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function ContactCard({
  property,
  defaultName,
  defaultPhone,
  defaultEmail,
}: {
  property: PropertyDetail;
  defaultName?: string;
  defaultPhone?: string;
  defaultEmail?: string;
}) {
  const [name, setName] = useState(defaultName ?? '');
  const [phone, setPhone] = useState(defaultPhone ?? '');
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [message, setMessage] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [moveIn, setMoveIn] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isHotel = property.category === 'Hotel';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSending(true);
    setError(null);

    try {
      await enquiryApi.create({
        propertyId: property.id,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        message: message.trim() || undefined,
        checkInDate: isHotel && checkIn ? checkIn : undefined,
        checkOutDate: isHotel && checkOut ? checkOut : undefined,
        moveInDate: !isHotel && moveIn ? moveIn : undefined,
      });

      setSent(true);
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setSending(false);
    }
  };

  const whatsAppMessage = `Hi, I found ${property.title} on RoomKart and would like to know more.`;

  return (
    <div className="sf-card sf-card-pad">
      <h2 className="sf-section-title">Contact the property</h2>

      <div className="d-grid gap-2 mb-3">
        <a className="btn btn-primary" href={`tel:${property.contact.phone}`}>
          Call {property.contact.phone}
        </a>

        {property.contact.whatsAppNumber && (
          <a
            className="btn btn-outline-success"
            href={whatsAppLink(property.contact.whatsAppNumber, whatsAppMessage)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Message on WhatsApp
          </a>
        )}
      </div>

      <div className="sf-divider" />

      {sent ? (
        <div className="alert alert-success mb-0" role="status">
          <strong>Enquiry sent.</strong> The property has your details and will get in touch. You can also call them
          directly using the number above.
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <h3 className="sf-section-title">Or send an enquiry</h3>

          <InlineError message={error} />

          <Field label="Your name" htmlFor="enquiry-name" required>
            <input
              id="enquiry-name"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={150}
            />
          </Field>

          <Field label="Mobile number" htmlFor="enquiry-phone" required hint="The property will call you on this number.">
            <input
              id="enquiry-phone"
              className="form-control"
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="10-digit mobile number"
            />
          </Field>

          <Field label="Email (optional)" htmlFor="enquiry-email">
            <input
              id="enquiry-email"
              className="form-control"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          {isHotel ? (
            <div className="row g-2">
              <div className="col-6">
                <Field label="Check in" htmlFor="enquiry-checkin">
                  <input
                    id="enquiry-checkin"
                    type="date"
                    className="form-control"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-6">
                <Field label="Check out" htmlFor="enquiry-checkout">
                  <input
                    id="enquiry-checkout"
                    type="date"
                    className="form-control"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                  />
                </Field>
              </div>
            </div>
          ) : (
            <Field label="When would you move in?" htmlFor="enquiry-movein">
              <input
                id="enquiry-movein"
                type="date"
                className="form-control"
                value={moveIn}
                onChange={(e) => setMoveIn(e.target.value)}
              />
            </Field>
          )}

          <Field label="Message (optional)" htmlFor="enquiry-message">
            <textarea
              id="enquiry-message"
              className="form-control"
              rows={3}
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Field>

          <button type="submit" className="btn btn-primary w-100" disabled={sending}>
            {sending ? 'Sending…' : 'Send enquiry'}
          </button>

          {isHotel && (
            <p className="form-text mb-0 mt-2">
              Your dates are passed on with the enquiry. RoomKart does not reserve rooms — the hotel will confirm
              availability with you directly.
            </p>
          )}
        </form>
      )}
    </div>
  );
}

// =====================================================================================
// Category fact blocks
// =====================================================================================

function PgFacts({ property }: { property: PropertyDetail }) {
  const pg = property.pgDetail!;

  const meals = [
    pg.breakfastIncluded && 'Breakfast',
    pg.lunchIncluded && 'Lunch',
    pg.dinnerIncluded && 'Dinner',
  ].filter(Boolean);

  return (
    <section className="mb-4">
      <h2 className="sf-section-title">About this hostel</h2>
      <dl className="row mb-0">
        <Fact label="Accommodation for" value={humanise(pg.genderPolicy)} />
        <Fact label="Property type" value={humanise(pg.propertyType)} />
        <Fact
          label="Food"
          value={pg.foodProvided ? (meals.length > 0 ? meals.join(', ') : 'Provided') : 'Not provided'}
        />
        {pg.foodChargePerMonth !== undefined && <Fact label="Food charge" value={`${money(pg.foodChargePerMonth)}/month`} />}
        {pg.foodNotes && <Fact label="Food details" value={pg.foodNotes} />}
        {pg.kitchenAccess && <Fact label="Kitchen access" value="Available" />}
        {pg.isVegetarianOnly && <Fact label="Food preference" value="Vegetarian only" />}
        {pg.securityDeposit !== undefined && pg.securityDeposit !== null && (
          <Fact label="Deposit" value={money(pg.securityDeposit)} />
        )}
        <Fact
          label="Electricity"
          value={[
            humanise(pg.electricityChargeType),
            pg.electricityFixedAmount !== undefined ? money(pg.electricityFixedAmount) : undefined,
            pg.electricityPerUnitRate !== undefined ? `${money(pg.electricityPerUnitRate)}/unit` : undefined,
          ].filter(Boolean).join(' · ')}
        />
        {pg.maintenanceCharge !== undefined && <Fact label="Maintenance" value={money(pg.maintenanceCharge)} />}
        {pg.minimumStayMonths && <Fact label="Minimum stay" value={`${pg.minimumStayMonths} months`} />}
        {pg.noticePeriodDays && <Fact label="Notice period" value={`${pg.noticePeriodDays} days`} />}
        {pg.hasCurfew && <Fact label="Curfew" value={pg.curfewTime ? `${pg.curfewTime.slice(0, 5)}` : 'Yes'} />}
        {pg.visitorPolicy && <Fact label="Visitors" value={pg.visitorPolicy} />}
        {pg.checkInRules && <Fact label="At check-in" value={pg.checkInRules} />}
        {pg.hasPowerBackup && <Fact label="Power backup" value="Available" />}
        {pg.hasHousekeeping && <Fact label="Housekeeping" value="Available" />}
        {pg.hasLaundry && <Fact label="Laundry service" value="Available" />}
      </dl>
    </section>
  );
}

function HomeFacts({ property }: { property: PropertyDetail }) {
  const home = property.homeDetail!;

  return (
    <section className="mb-4">
      <h2 className="sf-section-title">About this home</h2>
      <dl className="row mb-0">
        <Fact label="Configuration" value={`${home.bedrooms} BHK · ${home.bathrooms} bathroom${home.bathrooms === 1 ? '' : 's'}`} />
        <Fact label="Property type" value={humanise(home.homeType)} />
        <Fact label="Furnishing" value={humanise(home.furnishing)} />
        <Fact label="Rent" value={`${money(home.monthlyRent)}/month${home.isRentNegotiable ? ' (negotiable)' : ''}`} />
        <Fact label="Deposit" value={money(home.securityDeposit)} />
        <Fact
          label="Maintenance"
          value={home.maintenanceIncludedInRent ? 'Included in rent' : home.maintenanceCharge ? money(home.maintenanceCharge) : 'Not specified'}
        />
        <Fact label="Available from" value={plainDate(home.availableFrom)} />
        {home.floorNumber !== undefined && home.floorNumber !== null && (
          <Fact label="Floor" value={`${home.floorNumber}${home.totalFloors ? ` of ${home.totalFloors}` : ''}`} />
        )}
        {home.carpetAreaSqft && <Fact label="Carpet area" value={`${home.carpetAreaSqft} sq ft`} />}
        <Fact
          label="Parking"
          value={
            home.carParkingCount + home.bikeParkingCount === 0
              ? 'None'
              : [
                  home.carParkingCount > 0 && `${home.carParkingCount} car`,
                  home.bikeParkingCount > 0 && `${home.bikeParkingCount} two-wheeler`,
                ]
                  .filter(Boolean)
                  .join(', ')
          }
        />
        <Fact label="Water charges" value={home.waterChargeIncluded ? 'Included' : money(home.waterChargePerMonth)} />
        {home.hasLift && <Fact label="Lift" value="Available" />}
        {home.hasPowerBackup && <Fact label="Power backup" value="Available" />}
      </dl>

      {home.tenantPreferences.length > 0 && (
        <div className="mt-3">
          <h3 className="small text-uppercase sf-muted fw-bold">Owner prefers</h3>
          <div className="d-flex flex-wrap gap-2">
            {home.tenantPreferences.map((preference) => (
              <span className="sf-chip" key={preference.id}>
                {preference.name}
              </span>
            ))}
          </div>
          {home.tenantPreferenceNotes && <p className="form-text mt-1 mb-0">{home.tenantPreferenceNotes}</p>}
        </div>
      )}
    </section>
  );
}

function HotelFacts({ property }: { property: PropertyDetail }) {
  const hotel = property.hotelDetail!;

  return (
    <section className="mb-4">
      <h2 className="sf-section-title">About this hotel</h2>
      <dl className="row mb-0">
        <Fact label="Hotel type" value={humanise(hotel.hotelType)} />
        <Fact label="Check in" value={hotel.checkInTime.slice(0, 5)} />
        <Fact label="Check out" value={hotel.checkOutTime.slice(0, 5)} />
        <Fact label="Reception" value={hotel.is24HourReception ? '24 hours' : hotel.receptionHours ?? 'Not specified'} />
        <Fact label="Couples" value={hotel.couplesAllowed ? 'Welcome' : 'Not permitted'} />
        <Fact label="Pets" value={hotel.petsAllowed ? 'Allowed' : 'Not allowed'} />
        <Fact label="Local ID" value={hotel.localIdAccepted ? 'Accepted' : 'Not accepted'} />
        {hotel.hasRestaurant && <Fact label="Restaurant" value="Available" />}
        {hotel.hasRoomService && <Fact label="Room service" value="Available" />}
        {hotel.hasParking && <Fact label="Parking" value="Available" />}
        {hotel.hasConferenceRoom && <Fact label="Conference room" value="Available" />}
        {hotel.cancellationPolicy && <Fact label="Cancellation" value={hotel.cancellationPolicy} />}
        {hotel.childPolicy && <Fact label="Children" value={hotel.childPolicy} />}
      </dl>

      {hotel.starRating && hotel.starRatingSource === 'OwnerProvided' && (
        <p className="form-text mb-0 mt-2">
          The {hotel.starRating}-star rating is stated by the property and has not been verified by RoomKart.
        </p>
      )}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="col-5 col-sm-4 fw-normal sf-muted small py-1">{label}</dt>
      <dd className="col-7 col-sm-8 py-1 mb-0">{value}</dd>
    </>
  );
}

function RoomTypeCard({ roomType, isHotel }: { roomType: RoomType; isHotel: boolean }) {
  const availability = availabilityLabel(
    roomType.availabilityConfidence,
    roomType.availableUnits,
    roomType.unitLabel,
  );

  return (
    <div className="sf-card sf-card-pad">
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <div>
          <h3 className="h6 mb-1">{roomType.name}</h3>
          <p className="sf-muted small mb-1">
            {roomType.sharingType && sharingLabel(roomType.sharingType)}
            {roomType.hotelRoomCategory && humanise(roomType.hotelRoomCategory)}
            {' · '}
            {roomType.hasAirConditioning ? 'AC' : 'Non-AC'}
            {' · '}
            {humanise(roomType.bathroomType)} bathroom
          </p>

          {/* Bed layout and the guest limit are shown as separate facts so a twin room is
              never read as a two-guest cap, or the reverse. */}
          {isHotel && (
            <p className="sf-muted small mb-1">
              {roomType.bedConfiguration && <>Beds: {roomType.bedConfiguration}</>}
              {roomType.maxOccupancy && <> · Sleeps up to {roomType.maxOccupancy} guest{roomType.maxOccupancy === 1 ? '' : 's'}</>}
            </p>
          )}

          {roomType.description && <p className="small mb-1">{roomType.description}</p>}

          <span className={`sf-pill sf-pill--${availability.tone}`}>{availability.text}</span>
        </div>

        <div className="text-end">
          {isHotel ? (
            <>
              <div className="h5 mb-0">{money(roomType.totalPerNight ?? roomType.pricePerNight)}</div>
              <div className="sf-muted small">per night, taxes included</div>
              {roomType.extraBedAvailable && roomType.extraBedCharge && (
                <div className="sf-muted small">Extra bed {money(roomType.extraBedCharge)}</div>
              )}
            </>
          ) : (
            <>
              <div className="h5 mb-0">{money(roomType.monthlyRent)}</div>
              <div className="sf-muted small">per month</div>
              {roomType.securityDeposit !== undefined && roomType.securityDeposit !== null && (
                <div className="sf-muted small">Deposit {money(roomType.securityDeposit)}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================================
// Report
// =====================================================================================

function ReportDialog({ open, propertyId, onClose }: { open: boolean; propertyId: string; onClose: () => void }) {
  const [reason, setReason] = useState('IncorrectPrice');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);

    try {
      await propertyApi.report(propertyId, { reason, details: details.trim() || undefined });
      setDone(true);
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setDone(false);
    setDetails('');
    setError(null);
    onClose();
  };

  return (
    <ConfirmDialog
      open={open}
      title={done ? 'Thank you' : 'Report this listing'}
      confirmLabel={done ? 'Close' : 'Send report'}
      cancelLabel="Cancel"
      busy={busy}
      onConfirm={done ? close : submit}
      onCancel={close}
      message={
        done ? (
          <p className="mb-0">Our team will review this listing. Thank you for letting us know.</p>
        ) : (
          <>
            <InlineError message={error} />
            <InlineSuccess message={null} />

            <Field label="What is wrong?" htmlFor="report-reason" required>
              <select id="report-reason" className="form-select" value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="IncorrectPrice">The price is wrong</option>
                <option value="NotAvailable">It is no longer available</option>
                <option value="Fake">The listing looks fake</option>
                <option value="Duplicate">It is a duplicate</option>
                <option value="Offensive">The content is offensive</option>
                <option value="Other">Something else</option>
              </select>
            </Field>

            <Field label="Details (optional)" htmlFor="report-details">
              <textarea
                id="report-details"
                className="form-control"
                rows={3}
                maxLength={2000}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
              />
            </Field>
          </>
        )
      }
    />
  );
}

function groupBy<T>(items: T[], key: (item: T) => string): Record<string, T[]> {
  return items.reduce<Record<string, T[]>>((groups, item) => {
    const group = key(item);
    (groups[group] ??= []).push(item);
    return groups;
  }, {});
}
